/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  People as PeopleIcon,
  Send as SendIcon,
  Business as StudioIcon,
} from '@mui/icons-material';

// Add local dependencies.
import { useGetBrowsableStudiosQuery } from '../../../features/studios/studiosApi';
import { StudioSearchResult } from '../../../types/joinRequest';
import JoinRequestForm from '../../studios/joinRequests/JoinRequestForm';

interface PublicStudioBrowserProps {
  onJoinRequestSuccess?: () => void;
}

/**
 * PublicStudioBrowser component for browsing and joining public studios.
 */
const PublicStudioBrowser: React.FC<PublicStudioBrowserProps> = ({
  onJoinRequestSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [selectedStudio, setSelectedStudio] = useState<string | null>(null);

  const pageSize = 12;

  // Search for public studios
  const {
    data: studiosData,
    isLoading,
    error,
    refetch,
  } = useGetBrowsableStudiosQuery({
    search: searchTerm,
    page,
    pageSize,
  });

  const studios = studiosData?.results || [];
  const totalPages = studiosData ? Math.ceil(studiosData.count / pageSize) : 0;

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleDetailedJoin = (studioId: string) => {
    setSelectedStudio(studioId);
    setShowJoinForm(true);
  };

  const handleJoinFormSuccess = () => {
    setShowJoinForm(false);
    setSelectedStudio(null);
    refetch();
    onJoinRequestSuccess?.();
  };

  const getStudioStatus = (studio: StudioSearchResult) => {
    if (studio.isMember) {
      return { label: 'Member', color: 'success' as const };
    }
    if (studio.hasExistingRequest) {
      return { label: 'Request Pending', color: 'warning' as const };
    }
    return null;
  };

  return (
    <Box>
      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search studios by name, location, or dance style..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 600 }}
        />
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load studios. Please try again.
          <Button
            color="inherit"
            size="small"
            onClick={() => refetch()}
            sx={{ ml: 2 }}
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      )}

      {/* Results */}
      {!isLoading && !error && (
        <>
          {/* Results Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Public Studios
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {studiosData?.count || 0} studios available to join
            </Typography>
          </Box>

          {/* Studios Grid */}
          {studios.length > 0 ? (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 3,
                  mb: 4,
                }}
              >
                {studios.map(studio => {
                  const status = getStudioStatus(studio);
                  return (
                    <Card
                      key={studio.id}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        {/* Studio Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Avatar
                            src={studio.logo}
                            sx={{
                              width: 48,
                              height: 48,
                              bgcolor: 'primary.main',
                            }}
                          >
                            {studio.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              component="h3"
                              noWrap
                              title={studio.name}
                            >
                              {studio.name}
                            </Typography>
                            {status && (
                              <Chip
                                label={status.label}
                                color={status.color}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        </Box>

                        {/* Studio Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 2,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {studio.description || 'No description available'}
                        </Typography>

                        {/* Studio Stats */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 3,
                          }}
                        >
                          <PeopleIcon
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {studio.memberCount ??
                              (studio as any).member_count ??
                              0}{' '}
                            members
                          </Typography>
                        </Box>

                        {/* Actions */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                          }}
                        >
                          {!studio.isMember && !studio.hasExistingRequest && (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<SendIcon />}
                              onClick={() => handleDetailedJoin(studio.id)}
                              fullWidth
                            >
                              Request to Join
                            </Button>
                          )}
                          {studio.isMember && (
                            <Button variant="outlined" disabled fullWidth>
                              Already a Member
                            </Button>
                          )}
                          {studio.hasExistingRequest && (
                            <Button variant="outlined" disabled fullWidth>
                              Request Pending
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          ) : (
            /* Empty State */
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <StudioIcon
                sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {searchTerm
                  ? 'No studios match your search'
                  : 'No public studios available'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {searchTerm
                  ? 'Try adjusting your search terms'
                  : 'Check back later for new studios to join'}
              </Typography>
              {searchTerm && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                >
                  Clear Search
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      {/* Join Request Form Dialog */}
      <JoinRequestForm
        open={showJoinForm}
        preselectedStudioId={selectedStudio || undefined}
        onSuccess={handleJoinFormSuccess}
        onCancel={() => {
          setShowJoinForm(false);
          setSelectedStudio(null);
        }}
      />
    </Box>
  );
};

export default PublicStudioBrowser;
